<template>
    <KGallery
        v-if="images.length"
        :items="images"
        :label="label"
        class="photo-gallery"
        :class="{ 'photo-gallery--full-bleed': fullBleed }"
        @expand="index => emit('expand', { index, total: images.length })"
    >
        <template #media="{ item, index }">
            <!--
                The one image per page that may be eager is decided by the page, not by
                where this component happens to sit — `priority` gates loading,
                fetchpriority and the preload link together, because shipping only some of
                them is how two images end up fighting for bandwidth. It applies to the
                first slide only: the others are reached by interaction, never at load.
            -->
            <NuxtImg
                :src="item.src"
                :alt="item.alt"
                width="960"
                height="540"
                fit="cover"
                format="auto"
                sizes="xs:100vw sm:100vw md:768px"
                :loading="isPriority(index) ? 'eager' : 'lazy'"
                :fetchpriority="isPriority(index) ? 'high' : undefined"
                :preload="isPriority(index) ? { fetchPriority: 'high' } : false"
            />
        </template>

        <template #thumbnail="{ item }">
            <!-- Fixed size, so no `sizes`: the default densities emit the retina candidate. -->
            <NuxtImg
                :src="item.thumbSrc ?? item.src"
                alt=""
                width="104"
                height="78"
                fit="cover"
                format="auto"
                loading="lazy"
            />
        </template>

        <template #fullscreen="{ item }">
            <!--
                `fit="contain"` rather than the default: like scale-down it never crops, but
                unlike scale-down it also *enlarges*. Several gallery assets in Storyblok are
                only 600–1000px wide, and scale-down hands those back at their own size — so
                on a desktop viewer they sat in the middle of the stage at 45% of the width
                while the large ones filled it. KLightbox caps its image but never grows it,
                so the delivered pixel size is what decides. Width-only, because the viewer
                is width-constrained on the screens where this matters; on a phone the
                requested candidate is already smaller than these assets, so nothing is
                upscaled there.
            -->
            <NuxtImg
                :src="item.src"
                :alt="item.alt"
                width="1600"
                fit="contain"
                format="auto"
                sizes="xs:100vw sm:100vw md:100vw lg:100vw xl:100vw xxl:100vw"
                loading="lazy"
            />
        </template>
    </KGallery>
</template>

<script setup>
import { KGallery } from '@vemedia/karibu'

const props = defineProps({
    /** The story's main image. Leads the gallery. */
    featured: {
        type: Object,
        default: () => ({}),
    },

    /** Additional Storyblok assets, in order. */
    gallery: {
        type: Array,
        default: () => [],
    },

    /** Subject of the photographs, used to build the fallback alt text. */
    altBase: {
        type: String,
        default: '',
    },

    /** Accessible name for the gallery region. */
    label: {
        type: String,
        default: 'Photo gallery',
    },

    /**
     * This gallery holds the page's LCP image. Opt-in, never derived from render
     * position — see the comment in the `media` slot.
     */
    priority: {
        type: Boolean,
        default: false,
    },

    /**
     * On mobile, cancel the 16px gutter the content column adds so the stage runs out to
     * the page's own margin, square-cornered. Only pass it on a page that actually has
     * that gutter — `.tour` does, via its `padding-inline`. On a page without one the
     * negative margin has nothing to cancel and the stage overhangs its container.
     */
    fullBleed: {
        type: Boolean,
        default: false,
    },
})

const emit = defineEmits(['expand'])

const images = computed(() =>
    [props.featured, ...(props.gallery || [])]
        .filter(image => image?.filename)
        .map((image, index) => ({
            src: image.filename,
            // Editor-supplied alt always wins; otherwise fall back to a per-image label so
            // one identical alt is not repeated across every image in the gallery.
            alt: image.alt || (index === 0 ? props.altBase : `${props.altBase} — photo ${index + 1}`),
        })),
)

const isPriority = index => props.priority && index === 0
</script>

<style lang="scss" scoped>
@use '@vemedia/bcn-components/scss/responsive' as *;

.photo-gallery {
    margin-block-end: 1.5rem;

    @include mobile {
        // Squarer on a phone, where a 16:9 stage in a narrow column is a letterbox.
        --gallery-ratio: 4 / 3;
    }

    &--full-bleed {
        @include mobile {
            --gallery-radius: 0;
            // Keeps the thumbnails inside the gutter the stage just escaped.
            --gallery-rail-gutter: 16px;

            // Anchored to the container rather than the viewport, so a horizontally
            // overflowing page (a floating widget, say) cannot shift the centring.
            margin-block-start: -1rem;
            margin-inline: -16px;
            width: calc(100% + 32px);
        }
    }
}
</style>
